import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right.svg';
import { clsx } from 'clsx';

import Button from '../../../../components/Button/Button';
import StepItem from '../../../../components/StepItem/StepItem';
import { BUTTON_VARIANT } from '../../../../constants/interfaces';
import { IRO_QUESTION_TYPES } from '../../../../constants/materialAssessment';
import MAIROQuestionModal from './MAIROQuestionModal/MAIROQuestionModal';
import MAIROQuestionRate from './MAIROQuestionRate';
import MAIROQuestionSelect from './MAIROQuestionSelect';
import MAIROQuestionSwitcher from './MAIROQuestionSwitcher';

import type { IMAIroImpactQuestionsProps } from '../interfaces/IMAIroImpactQuestionsProps';
import type { IMAIroQuestion } from '../interfaces/IMAIroQuestions';

import styles from '../scss/MAIROQuestion.module.scss';

function MAIROImpactQuestions({
  questions,
  control,
  currentStep,
  onNextStep,
  onPrevStep,
  onOpenReasonModal,
  setValue,
  fields,
  isReasonModalOpen,
  handleOpenFinishModal,
  hideThirdStep,
  handleHideThirdStep,
}: IMAIroImpactQuestionsProps) {
  const { t } = useTranslation();
  const [currentModalQuestionIndex, setCurrentModalQuestionIndex] = useState<number>(0);

  const handleOpenModal = (isOpen: boolean, questionIndex?: number): void => {
    if (questionIndex !== undefined) {
      setCurrentModalQuestionIndex(questionIndex);
    }
    onOpenReasonModal(isOpen);
  };

  const currentModalQuestion = questions[currentModalQuestionIndex] || questions[0];

  return (
    <div className={clsx(styles.root, styles.impactQuestions)}>
      <div className={styles.stepsWrapper}>
        <div className={styles.steps}>
          <StepItem
            title={t('materialityAssessment.overview')}
            step={1}
            disabled={currentStep < 1}
          />
          <ArrowRightIcon />
          <StepItem
            title={t('materialityAssessment.severity')}
            step={2}
            disabled={currentStep < 2}
          />
          {hideThirdStep ? (
            <>
              <ArrowRightIcon />
              <StepItem
                title={t('materialityAssessment.likelihood')}
                step={3}
                disabled={currentStep < 3}
              />
            </>
          ) : null}
        </div>
        <p className={styles.subTitle}>{t('materialityAssessment.stepsDescription')}</p>
      </div>
      {questions.map((question: IMAIroQuestion, index: number) => {
        const questionsData = fields?.questions || [];
        const questionsLength = questionsData.length || 0;

        const currentValue = (index >= 0 && questionsLength > 0)
          ? questionsData[index]?.answer?.selectedOptionId : undefined;

        return (
          currentStep === question.step ? (
            <>
              {question.questionType === IRO_QUESTION_TYPES.SWITCH && (
                <MAIROQuestionSwitcher
                  key={question.id}
                  question={question}
                  control={control}
                  onOpenReasonModal={handleOpenModal}
                  setValue={setValue}
                  questionIndex={index}
                  currentValue={currentValue}
                  handleHideThirdStep={handleHideThirdStep}
                />
              )}
              {question.questionType === IRO_QUESTION_TYPES.SELECT && (
                <MAIROQuestionSelect
                  key={question.id}
                  question={question}
                  control={control}
                  setValue={setValue}
                  questionIndex={index}
                  currentValue={currentValue}
                />
              )}
              {question.questionType === IRO_QUESTION_TYPES.RATE && (
                <MAIROQuestionRate
                  key={question.id}
                  question={question}
                  control={control}
                  setValue={setValue}
                  questionIndex={index}
                  currentValue={currentValue}
                />
              )}
            </>
          ) : null
        );
      })}
      <MAIROQuestionModal
        control={control}
        open={isReasonModalOpen}
        onOpenReasonModal={handleOpenModal}
        questionIndex={currentModalQuestionIndex}
        setValue={setValue}
        question={currentModalQuestion}
        handleHideThirdStep={handleHideThirdStep}
      />
      <div className={clsx(styles.footer, {
        [styles.onlyNext]: currentStep === 1,
      })}
      >
        {currentStep > 1 ? (
          <Button
            title={t('common.prev')}
            className={styles.nextStep}
            variant={BUTTON_VARIANT.secondary}
            onClick={onPrevStep}
          />
        ) : null}
        <Button
          title={
            currentStep === 1 || (currentStep === 2 && hideThirdStep)
              ? t('materialityAssessment.nextStep')
              : t('common.finish')
          }
          className={styles.nextStep}
          variant={BUTTON_VARIANT.primary}
          onClick={currentStep === 1 || (currentStep === 2 && hideThirdStep)
            ? () => onNextStep() : () => handleOpenFinishModal(true)}
        />
      </div>
    </div>
  );
}

export default MAIROImpactQuestions;
